import { useEffect, useRef, useState } from "react";
import { avatarSrc } from "./avatar";
import { faNum } from "./cards";
import { SUBSCRIBER_MARK } from "./seats";
import { tweenRaw, waitRaw } from "../scene/fly";
import { inOutCubic, outCubic } from "../scene/ease";
import { endIntro, type IntroPlan, type IntroSeat } from "./stake";

/**
 * پرده‌ی شرطِ آغازِ میزِ آنلاین.
 *
 * ترتیبش همان ترتیبِ چیزی است که بازیکن باید بفهمد: **با کی** (چهره‌ها از دو
 * سو می‌آیند)، **مقابلِ هم** (VS می‌کوبد)، **سرِ چه** (گلدانِ خالی باز
 * می‌شود)، و بعد آن یک قلمی که کلِ ماجرا را ملموس می‌کند: **سکه‌ها از کیفِ
 * هر بازیکن بیرون می‌ریزند و دورِ گلدان کومه می‌شوند** — کیف‌ها تا صفر پایین
 * و گلدان تا عددِ کامل بالا. عددِ ورودی پیش از این فقط یک خط در اتاق بود؛
 * چیزی که هیچ‌وقت *حس* نمی‌شد.
 *
 * زمان‌ها خام‌اند (`tweenRaw`/`waitRaw`) و نه ضریبِ سرعتِ بازیکن: «خیلی آرام»
 * برای پخشِ ورق انتخاب شده، نه برای این‌که پرده‌ی معارفه ده ثانیه طول بکشد.
 *
 * لمس ردش می‌کند. میزِ زیرِ پرده ساخته شده ولی هیچ ورقی پخش نشده — صفِ صحنه
 * پشتِ دروازه‌ی [[stake]] ایستاده و با پایانِ همین پرده راه می‌افتد.
 */

const ENTER_MS = 640;      // ورودِ چهره‌ها + ضربه‌ی VS
const POT_MS = 260;        // بازشدنِ گلدانِ خالی
const RAIN_MS = 1450;      // رگبارِ سکه
const SETTLE_MS = 620;     // نشستنِ گلدان
const FREE_HOLD = 820;     // میزِ رایگان: همان معارفه، بی‌سکه
const FADE_MS = 300;

const COIN_FLY_MS = 540;
const COIN_SIZE = 26;
/** سقفِ سکه‌های هم‌زمانِ روی صفحه — بینِ کیف‌ها تقسیم می‌شود. */
const COIN_BUDGET = 44;

/**
 * نگهبانِ پرده: از این دیرتر، پرده به‌زور جمع می‌شود.
 *
 * چرا لازم است: دنباله پشتِ tweenهای `requestAnimationFrame` می‌ایستد، و rAF
 * در زبانه‌ی پنهان **اصلاً اجرا نمی‌شود**. بازیکنی که وسطِ معارفه از اپ بیرون
 * می‌رود، برمی‌گردد و می‌بیند پرده سرِ جایش مانده — و چون صفِ صحنه پشتِ همین
 * پرده ایستاده، میز هم شروع نشده. نگهبان با `setTimeout` کار می‌کند که در
 * زبانه‌ی پنهان هم (کُند، ولی) می‌آید.
 */
const BAIL_MS = 6000;

export function StakeIntro({ plan }: { plan: IntroPlan }) {
  const [done, setDone] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const fly = useRef<HTMLDivElement>(null);
  const pot = useRef<HTMLDivElement>(null);
  const potNum = useRef<HTMLElement>(null);
  const purses = useRef(new Map<number, HTMLElement>());
  /** لمسِ بازیکن: از هر جای دنباله، یک‌راست به پایان. */
  const skipped = useRef(false);
  /** جمع‌کردنِ پرده، از بیرونِ دنباله — لمس نباید منتظرِ tweenِ در جریان بمانَد. */
  const finishRef = useRef<() => void>(() => { skipped.current = true; });

  useEffect(() => {
    let cancelled = false, ended = false;
    const stop = () => cancelled || skipped.current || ended;
    const calm = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    // اجرای دومِ StrictMode: سکه‌های اجرای اول نباید روی هم بمانند.
    if (fly.current) fly.current.textContent = "";

    /**
     * جمع‌کردنِ پرده — هم پایانِ طبیعی، هم لمسِ بازیکن، هم نگهبان.
     *
     * عددها همیشه سرِ جای آخرشان می‌نشینند: کسی که پرده را رد کرد نباید
     * نیم‌ثانیه گلدانِ نیمه‌پر ببیند. و `endIntro` هر جور که تمام شویم صدا
     * می‌شود، چون صفِ صحنه پشتِ همان ایستاده.
     */
    const finish = () => {
      if (ended || cancelled) return;
      ended = true;
      settle(plan, potNum.current, purses.current);
      pot.current?.classList.add("full");
      root.current?.classList.add("out");
      window.setTimeout(() => {
        if (cancelled) return;
        endIntro();
        setDone(true);
      }, FADE_MS);
    };
    finishRef.current = finish;
    const bail = window.setTimeout(finish, BAIL_MS);

    void (async () => {
      await waitRaw(ENTER_MS);
      if (stop()) return;

      if (plan.stake > 0n) {
        pot.current?.classList.add("on");
        if (!stop()) await waitRaw(POT_MS);
        // «حرکتِ کمتر»: رگبار همان چیزی است که این تنظیم درباره‌اش است. عدد
        // می‌مانَد و پرواز می‌رود — پرده هنوز می‌گوید سرِ چه بازی می‌کنی.
        if (calm) { settle(plan, potNum.current, purses.current); if (!stop()) await waitRaw(FREE_HOLD); }
        else if (!stop()) await rain(plan, fly.current, pot.current, potNum.current, purses.current, stop);
        if (!stop()) await waitRaw(SETTLE_MS);
      } else if (!stop()) {
        await waitRaw(FREE_HOLD);
      }
      finish();
    })();

    return () => { cancelled = true; window.clearTimeout(bail); };
    // نقشه با کلیدِ میز ثابت است؛ پرده یک بار می‌آید و می‌رود.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (done) return null;

  const side = (rows: IntroSeat[], cls: string) => (
    <div className={`sintro-side ${cls}`}>
      {rows.map((s, i) => (
        <Plate key={s.seat} s={s} stake={plan.stake} delay={i * 90}
          purseRef={(el) => { if (el) purses.current.set(s.seat, el); else purses.current.delete(s.seat); }} />
      ))}
    </div>
  );

  return (
    <div ref={root} className="sintro" onPointerDown={() => { skipped.current = true; finishRef.current(); }}>
      {side(plan.theirs, "them")}

      <div className="sintro-mid">
        <span className="sintro-vs" aria-hidden>
          <i className="sintro-ring" />
          VS
        </span>
        {plan.stake > 0n && (
          <div ref={pot} className="sintro-pot">
            <span className="sintro-stack" aria-hidden><i /><i /><i /></span>
            <b ref={potNum}>{faNum(0)}</b>
          </div>
        )}
      </div>

      {side(plan.mine, "mine")}

      <div ref={fly} className="sintro-fly" aria-hidden />

      {/* انیمیشن چیزی نمی‌گوید؛ این خط برای صفحه‌خوان است. */}
      <p className="sintro-sr">
        {plan.stake > 0n
          ? `میزِ ورودی‌دار: هر بازیکن ${faNum(plan.stake)} سکه، گلدان ${faNum(plan.pot)} سکه.`
          : "میز آماده است."}
      </p>
    </div>
  );
}

function Plate({ s, stake, delay, purseRef }: {
  s: IntroSeat; stake: bigint; delay: number; purseRef: (el: HTMLElement | null) => void;
}) {
  return (
    <div className={`sintro-p${s.me ? " me" : ""}`} style={{ animationDelay: `${delay}ms` }}>
      <span className="sintro-av">
        <img src={avatarSrc(s.avatar)} alt="" width="72" height="72" decoding="async" />
      </span>
      <span className="sintro-nm">
        {s.name}{s.subscriber && !s.bot ? " " + SUBSCRIBER_MARK : ""}
      </span>
      {stake > 0n && (
        <span className="sintro-purse" ref={purseRef}>
          <i className="sic" aria-hidden /><b>{faNum(stake)}</b>
        </span>
      )}
    </div>
  );
}


// ───────────────────────── رگبارِ سکه ─────────────────────────
//
// نسخه‌ی اول سکه‌ها را در یک مستطیلِ ۱۳۲×۴۴ تصادفی می‌پاشید و همان‌جا رها
// می‌کرد. نتیجه یک **لکه‌ی طلاییِ نامرتب** بود: نه کومه‌ای که چشم بشناسد، نه
// لحظه‌ای که حس شود چیزی *فرود آمد*. این نسخه سه چیز را عوض می‌کند:
//
//   • **جای فرود حساب‌شده است.** هر سکه اسلاتِ خودش را در یک کومه‌ی هرمی
//     می‌گیرد؛ سکه‌ها روی هم می‌افتند و یک دسته‌ی واقعی می‌سازند.
//   • **فرود ضربه دارد.** گلدان با هر سکه یک تکانِ کوتاه می‌خورد و عدد
//     می‌پرد. بی این، رگبار فقط «چیزی از این‌طرف به آن‌طرف رفت» بود.
//   • **پرتاب خیز دارد.** سکه اول کمی **عقب** می‌کشد و بعد پرتاب می‌شود —
//     همان قاعده‌ی کهنه‌ی انیمیشن که بی آن هر حرکتی بی‌جان است.

/** مرکزِ یک عنصر، در مختصاتِ لایه‌ی پرواز. */
function centerIn(layer: HTMLElement, el: HTMLElement): { x: number; y: number } {
  const a = layer.getBoundingClientRect(), b = el.getBoundingClientRect();
  return { x: b.left - a.left + b.width / 2, y: b.top - a.top + b.height / 2 };
}

/** عددِ شبه‌تصادفیِ پایدار از یک بذر — تا هر اجرا همان شکل را بسازد. */
const rnd = (seed: number, salt: number) => {
  const v = Math.sin((seed + 1) * (12.9898 + salt * 7.13)) * 43758.5453;
  return v - Math.floor(v);
};

/**
 * جای هر سکه در کومه.
 *
 * کومه‌ی هرمی: ردیفِ پایین پهن‌تر، هر ردیف نیم‌سکه جابه‌جا، و هر ردیف کمی
 * بالاتر. سکه‌ها **روی هم** می‌افتند (فاصله کمتر از قطر) تا دسته دیده شود و
 * نه دانه‌های جدا.
 */
/** ظرفیتِ هر ردیفِ کومه، از پایین. */
const ROW = [7, 6, 5, 4];
/** بلندیِ کومه — در فاصله‌ی فرود حساب می‌شود تا کومه وارد جعبه نشود. */
const STACK_H = (ROW.length - 1) * COIN_SIZE * 0.34;

function slot(i: number): { dx: number; dy: number } {
  let row = 0, left = i;
  while (row < ROW.length - 1 && left >= ROW[row]) { left -= ROW[row]; row++; }
  const n = ROW[row];
  const step = COIN_SIZE * 0.62;          // هم‌پوشانی: دسته، نه دانه
  return {
    dx: (left - (n - 1) / 2) * step + (rnd(i, 3) - 0.5) * 3,
    dy: -row * COIN_SIZE * 0.34 + (rnd(i, 4) - 0.5) * 2,
  };
}

/** تکانِ کوتاهِ گلدان و پرشِ عدد — بازخوردِ «چیزی فرود آمد». */
function punch(potEl: HTMLElement | null, numEl: HTMLElement | null): void {
  if (potEl) {
    potEl.classList.remove("hit");
    void potEl.offsetWidth;               // ری‌استارتِ انیمیشن
    potEl.classList.add("hit");
  }
  if (numEl) {
    numEl.classList.remove("pop");
    void numEl.offsetWidth;
    numEl.classList.add("pop");
  }
}

async function rain(
  plan: IntroPlan,
  layer: HTMLElement | null,
  potEl: HTMLElement | null,
  potNum: HTMLElement | null,
  purses: Map<number, HTMLElement>,
  stop: () => boolean,
): Promise<void> {
  if (!layer || !potEl) return;
  const to = centerIn(layer, potEl);
  // کومه کاملاً **زیرِ** جعبه می‌نشیند: نصفِ قدِ جعبه، به‌اضافه‌ی نصفِ قطرِ
  // سکه، به‌اضافه‌ی کمی فاصله. نسخه‌ی قبل نصفِ قطرِ سکه را نداشت و روی
  // نمایشگرِ باریک، کومه درست روی عددِ گلدان می‌نشست — همان عددی که کلِ این
  // پرده برای نشان‌دادنش هست.
  // نصفِ قدِ جعبه + نصفِ قطرِ سکه + بلندیِ کومه + کمی فاصله.
  //
  // `STACK_H` را جدا می‌شمریم چون کومه **به بالا** رشد می‌کند: بی آن،
  // ردیفِ آخر دوباره می‌آمد توی جعبه و روی عدد می‌نشست — همان عددی که کلِ
  // این پرده برای نشان‌دادنش هست.
  const lip = potEl.getBoundingClientRect().height / 2 + COIN_SIZE / 2 + STACK_H + 8;
  const seats = [...plan.mine, ...plan.theirs];
  const landed: HTMLElement[] = [];
  const potN = Number(plan.pot), stakeN = Number(plan.stake);

  const counters = Promise.all([
    tweenRaw(RAIN_MS, (k) => {
      if (stop() || !potNum) return;
      potNum.textContent = faNum(Math.round(potN * outCubic(k)));
    }),
    tweenRaw(RAIN_MS, (k) => {
      if (stop()) return;
      const txt = faNum(Math.round(stakeN * (1 - outCubic(k))));
      for (const el of purses.values()) {
        const b = el.querySelector("b");
        if (b) b.textContent = txt;
      }
    }),
  ]);

  const flights: Promise<void>[] = [];
  // سقفِ کلِ صفحه، نه فقط هر کیف: میزِ شش‌نفره با ۱۲ سکه برای هر نفر ۷۲ سکه
  // روی گلدان می‌ریخت و عددش زیرِ طلا گم می‌شد.
  const per = Math.max(3, Math.min(plan.coins, Math.round(COIN_BUDGET / seats.length)));
  const gap = Math.max(22, (RAIN_MS - COIN_FLY_MS) / per);
  let order = 0;
  for (let j = 0; j < per; j++) {
    if (stop()) break;
    for (const s of seats) {
      const purse = purses.get(s.seat);
      if (!purse) continue;
      flights.push(one(layer, centerIn(layer, purse), to, lip, order++, landed, potEl, potNum, stop));
    }
    await waitRaw(gap);
  }
  if (stop()) return;

  await counters;
  await Promise.all(flights);
  if (stop()) return;

  // قفلِ پایان: گلدان می‌درخشد، دسته‌سکه بالا می‌پرد، و کومه در خودش جمع
  // می‌شود — یک حرکتِ کوتاه که می‌گوید «تمام، این مالِ برنده است».
  potEl.classList.add("full");
  punch(potEl, potNum);
  await tweenRaw(240, (k) => {
    if (stop()) return;
    const p = outCubic(k);
    for (const c of landed) {
      const sx = Number(c.dataset.x), sy = Number(c.dataset.y);
      const dx = (to.x - sx) * 0.34 * p, dy = (to.y + lip - sy) * 0.34 * p;
      c.style.transform = `translate(${sx + dx - COIN_SIZE / 2}px, ${sy + dy - COIN_SIZE / 2}px) scale(${1 - 0.18 * p})`;
    }
  });
  for (const c of landed) c.classList.add("gone");
  await waitRaw(260);
  for (const c of landed) c.remove();
}

/**
 * یک سکه: خیز، قوس، فرود در اسلاتِ خودش، و ضربه‌ی گلدان.
 *
 * هر سه فاز در **یک** تویین‌اند و نه سه تویینِ زنجیرشده. زنجیر شکننده بود:
 * تویینِ دوم از داخلِ فریمِ rAF ِ تویینِ اول ساخته می‌شد، و تایم‌استمپی که
 * rAF می‌دهد لحظه‌ی شروعِ همان فریم است — یعنی گاهی **عقب‌تر** از
 * `performance.now()` ِ لحظه‌ی ساخت. آن‌وقت k از صفر جلو نمی‌رفت و سکه سرِ
 * جای پرتاب یخ می‌زد. یک تویین، یک ساعت، بی وابستگی به ترتیبِ فریم‌ها.
 *
 * قوس با کنترلِ افقی هم خم می‌شود و نه فقط عمودی: سکه‌هایی که همه روی یک
 * صفحه بالا و پایین بروند، مثلِ یک نوارِ متحرک دیده می‌شوند و نه مثلِ چیزی
 * که پرتاب شده.
 */
function one(
  layer: HTMLElement, from: { x: number; y: number }, to: { x: number; y: number },
  lip: number, seed: number, landed: HTMLElement[],
  potEl: HTMLElement | null, potNum: HTMLElement | null, stop: () => boolean,
): Promise<void> {
  const el = document.createElement("i");
  el.className = "sic fly";
  layer.appendChild(el);

  const r1 = rnd(seed, 0), r2 = rnd(seed, 1), r3 = rnd(seed, 2);

  const sx = from.x + (r1 - 0.5) * 30, sy = from.y + (r2 - 0.5) * 16;
  const st = slot(seed);
  const ex = to.x + st.dx, ey = to.y + lip + st.dy;

  // خیز: خلافِ مسیرِ پرواز، تا پرتاب جان بگیرد.
  const len = Math.hypot(ex - sx, ey - sy) || 1;
  const kx = sx - ((ex - sx) / len) * 14, ky = sy - ((ey - sy) / len) * 14;

  const arc = 84 + r3 * 76;                 // بلندیِ قوس
  const bend = (r1 - 0.5) * 54;             // خمِ افقی
  const spin = 420 + r3 * 620;
  const fly = COIN_FLY_MS * (0.84 + r3 * 0.34);
  const kick = 110;
  const total = kick + fly;
  const split = kick / total;               // مرزِ دو فاز روی خطِ زمان

  const put = (x: number, y: number, rot: number, sc: number) => {
    el.style.transform =
      `translate(${x - COIN_SIZE / 2}px, ${y - COIN_SIZE / 2}px) rotateY(${rot}deg) scale(${sc})`;
  };
  put(sx, sy, 0, 0.2);

  return tweenRaw(total, (k) => {
    if (stop()) return;
    if (k < split) {
      // ۱) خیز: از هیچ بیرون می‌آید و کمی عقب می‌کشد.
      const p = outCubic(k / split);
      put(sx + (kx - sx) * p, sy + (ky - sy) * p, 90 * p, 0.2 + 0.85 * p);
      return;
    }
    // ۲) پرواز: قوسِ عمودی + خمِ افقی، شتاب می‌گیرد و آرام می‌نشیند.
    const p = inOutCubic((k - split) / (1 - split));
    const hump = Math.sin(p * Math.PI);
    put(
      kx + (ex - kx) * p + bend * hump,
      ky + (ey - ky) * p - hump * arc,
      90 + spin * p,
      1.05 - 0.05 * p,
    );
  }).then(() => {
    if (stop()) return;
    // ۳) فرود: کوبشِ کوتاهِ خودِ سکه، و ضربه‌ی گلدان.
    el.classList.remove("fly");
    el.dataset.x = String(ex);
    el.dataset.y = String(ey);
    landed.push(el);
    el.classList.add("land");
    punch(potEl, potNum);
  });
}

/** عددها را سرِ جای نهایی‌شان بگذار — سرِ پرش یا پایانِ طبیعی. */
function settle(plan: IntroPlan, potNum: HTMLElement | null, purses: Map<number, HTMLElement>): void {
  if (plan.stake <= 0n) return;
  if (potNum) potNum.textContent = faNum(plan.pot);
  for (const el of purses.values()) {
    const b = el.querySelector("b");
    if (b) b.textContent = faNum(0);
  }
}
